import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import BlogMainSection from "../../components/blogs/BlogsMainSection";
import PageContent from "../../components/blogs/PageContent";
import SideBar from "../../components/blogs/SideBar";
import BlogBody from "../../components/blogs/BlogBody";
import RelatedBlock from "../../components/content/RelatedBlock";
import FindLawyerBlock from "../../components/content/FindLawyerBlock";
import { breadcrumbJsonLd } from "../../components/content/contentLinks";
import ComponentsLoader from "../../components/includes/ComponentsLoader";
import NotFound from "../NotFound/NotFound";
import { useBlogContext } from "../../shared/context/BlogContext";
import { useParams } from "react-router-dom";
import Seo from "../../shared/seo/Seo";
import { urls, copy, currentLang } from "../../shared/seo/links";
import useCanonical from "../../shared/seo/useCanonical";
import { citationLd, lastUpdated } from "@seo/sources";

/** { ar, fr, en } paths of a guide's published languages, or null with no translation. */
export function siblingAlternates(siblings) {
  const list = (siblings || []).filter((s) => urls.LANGS.includes(s.language));
  if (list.length < 2) return null;
  return list.reduce((acc, s) => ({ ...acc, [s.language]: urls.blogPath(s) }), {});
}

export default function Blog() {
  const { t } = useTranslation();
  const { getBlogsById, getBlogSiblings, getBlogRelated } = useBlogContext();
  const [blog, setBlog] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [related, setRelated] = useState({});
  const [notFound, setNotFound] = useState(false);
  const { id } = useParams();
  useEffect(() => {
    let live = true;
    async function fetchData() {
      let res = await getBlogsById({ id: id });
      if (!live) return;
      // axiosFactory returns the error message instead of data on a 404.
      const found = res.data?.blog;
      if (found && found.title) {
        setBlog(found);
      } else {
        setNotFound(true);
      }
    }
    // Translations and related content: links only, the article shows without them.
    async function fetchLinks() {
      const [sib, rel] = await Promise.all([getBlogSiblings({ id }), getBlogRelated({ id })]);
      if (!live) return;
      setSiblings(sib.data?.siblings ?? []);
      setRelated(rel.data?.status === "success" ? rel.data : {});
    }
    setBlog(null);
    setSiblings([]);
    setRelated({});
    setNotFound(false);
    fetchData();
    fetchLinks();
    return () => {
      live = false;
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // An article lives in its own language; a guide links its translations.
  const canonical = blog ? urls.blogPath(blog) : null;
  useCanonical(canonical);

  if (notFound) return <NotFound />;
  if (!blog) {
    return (
      <PageContent>
        <Seo title={copy.blogs(currentLang()).title} />
        <BlogMainSection>
          <ComponentsLoader />
        </BlogMainSection>
        <SideBar />
      </PageContent>
    );
  }

  const lang = urls.blogLang(blog);
  const category = urls.findCategory(blog.category);
  const crumbs = [
    { name: t("Home"), path: urls.homePath(lang) },
    { name: copy.blogs(lang).h1, path: urls.sectionPath("blogs", lang) },
    ...(category ? [{ name: urls.categoryName(category, lang), path: urls.categoryPath(category, lang) }] : []),
    { name: blog.title, path: canonical },
  ];
  const description = copy.blog(lang, blog).description;
  const updated = lastUpdated(blog);
  const citation = citationLd(blog.sources);

  return (
    <PageContent>
      <Seo
        title={copy.blog(lang, blog).title}
        description={description}
        path={canonical}
        alternates={siblingAlternates(siblings)}
        // Social networks do not render SVG covers: their JPEG twin.
        image={urls.ogImagePath(blog.image) || undefined}
        type="article"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: blog.title,
            description,
            image: urls.ogImagePath(blog.image) ? urls.absoluteUrl(window.location.origin, urls.ogImagePath(blog.image)) : undefined,
            datePublished: blog.createdAt,
            dateModified: updated ? updated.toISOString() : undefined,
            inLanguage: lang,
            mainEntityOfPage: urls.absoluteUrl(window.location.origin, canonical),
            ...(category ? { articleSection: urls.categoryName(category, lang) } : {}),
            ...(citation.length ? { citation } : {}),
            author: { "@type": "Organization", name: copy.SITE_NAME },
            publisher: { "@type": "Organization", name: copy.SITE_NAME },
          },
          breadcrumbJsonLd(crumbs),
        ]}
      />
      <BlogMainSection>
        <BlogBody blog={blog} crumbs={crumbs} siblings={siblings} category={category} />
        {/* BlogMainSection is a .row: full-width columns keep the gutters. */}
        <div className="col-12">
          <RelatedBlock items={related.related} />
          <FindLawyerBlock cities={related.cities} />
        </div>
      </BlogMainSection>
      <SideBar />
    </PageContent>
  );
}
